import type { BrowserFamily, MicPlatform } from "./micPlatform";
import type { MicStateId } from "./micStatus";

export type MicAction = "change" | "allow" | "check_again" | "get_help" | "try_again";
export type MicTone = "dark" | "cream" | "pink";
export interface MicCopy {
    tone: MicTone;
    title: string;
    body?: string;
    steps?: string[];
    note?: string;
    wayOut?: string;
    actions: MicAction[];
}

export const IPHONE_WAY_OUT = "Can't fix it now? Record this visit on the HappyDoc iPhone app.";
export const MIC_LIST_TITLE = "Choose a microphone";
export const MIC_LIST_FOOTER =
    "Remembered on this computer. While this list is open, the bars show what the mic hears.";
export const ACTION_LABELS: Record<MicAction, string> = {
    change: "Change",
    allow: "Allow",
    check_again: "Check again",
    get_help: "Get help",
    try_again: "Try again",
};

// Idle label when no mic is saved and the browser does not name its default (Firefox, Safari).
export const DEFAULT_MIC_LABEL = "Default microphone";
export const readyLine = (micLabel: string): string => `Ready · ${micLabel}`;
export const hearingLine = (micLabel: string): string => `Hearing you · ${micLabel}`;
// R23: a minute of continuous silence while recording. Informational only, no action.
export const SILENCE_LINE = "No sound for a minute. Still recording.";
export const pausedLine = (micLabel: string): string => `Paused · ${micLabel}`;

const BROWSER_NAMES: Partial<Record<BrowserFamily, string>> = {
    chrome: "Chrome",
    firefox: "Firefox",
    safari: "Safari",
    edge: "Edge",
};

// Name as listed in macOS System Settings, which differs from the short name for Chrome and Edge.
const MAC_APP_NAMES: Partial<Record<BrowserFamily, string>> = {
    chrome: "Google Chrome",
    edge: "Microsoft Edge",
    firefox: "Firefox",
    safari: "Safari",
};

const blockedInBrowser = (browser: BrowserFamily): Pick<MicCopy, "title" | "steps"> => {
    switch (browser) {
        case "chrome":
        case "edge":
            return {
                title: `${BROWSER_NAMES[browser]} is blocking your microphone`,
                steps: ["Click the site settings icon at the left of the web address", "Turn on Microphone"],
            };
        case "firefox":
            return {
                title: "Firefox is blocking your microphone",
                steps: [
                    "Click the permissions icon at the left of the web address",
                    "Clear the blocked Microphone permission",
                ],
            };
        case "safari":
            return {
                title: "Safari is blocking your microphone",
                steps: ["In the Safari menu, choose Settings for This Website", "Set Microphone to Allow"],
            };
        default:
            return {
                title: "Your browser is blocking your microphone",
                steps: ["Open this site's settings in your browser", "Allow the microphone"],
            };
    }
};

export const micCopy = (state: MicStateId, platform: MicPlatform): MicCopy => {
    switch (state) {
        case "R10":
            return { tone: "dark", title: "", actions: ["change"] };
        case "R12":
            return {
                tone: "dark",
                title: "Let HappyDoc use your mic",
                body: `${BROWSER_NAMES[platform.browser] ?? "Your browser"} will ask once.`,
                actions: ["allow"],
            };
        case "R13":
            if (platform.os === "ios") return micCopy("R31", platform);
            return {
                tone: "pink",
                ...blockedInBrowser(platform.browser),
                wayOut: IPHONE_WAY_OUT,
                actions: ["check_again", "get_help"],
            };
        case "R14": {
            // R14 copy uses the browser name mid-sentence, so the fallback stays lowercase.
            const name = BROWSER_NAMES[platform.browser] ?? "your browser";
            const appName = MAC_APP_NAMES[platform.browser] ?? "your browser";
            return {
                tone: "pink",
                title: `Your Mac is blocking ${name} from the mic`,
                body: `This is a Mac setting, so ${name}'s own settings can't fix it.`,
                steps: [
                    "Open System Settings, then Privacy & Security, then Microphone",
                    `Turn on ${appName}`,
                    `Quit ${name} and open it again`,
                ],
                note: "If your clinic's IT manages this computer, they may need to do this for you.",
                wayOut: IPHONE_WAY_OUT,
                actions: ["check_again", "get_help"],
            };
        }
        case "R14W":
            return {
                tone: "pink",
                title: "Windows is blocking the mic, or another app is using it",
                steps: [
                    "Settings, Privacy & security, Microphone",
                    'Turn on "Let desktop apps access your microphone"',
                    "Close Zoom, Teams or Weave",
                ],
                wayOut: IPHONE_WAY_OUT,
                actions: ["check_again", "get_help"],
            };
        case "R31":
            return {
                tone: "pink",
                title: "Safari is blocking your microphone",
                steps: ["Tap aA at the left of the address bar", "Tap Website Settings", "Set Microphone to Allow"],
                wayOut: IPHONE_WAY_OUT,
                actions: ["check_again", "get_help"],
            };
        case "R16":
            return {
                tone: "pink",
                title: "No microphone found",
                body: "Plug one in. We'll pick it up.",
                actions: ["get_help"],
            };
        case "R17":
            return {
                tone: "pink",
                title: "Another app is using your mic",
                body: "Close Zoom, Teams or phone software like Weave.",
                actions: ["try_again"],
            };
        default:
            // Generic fallback for unclassified errors. MicStatusBar never renders the raw error text.
            return { tone: "pink", title: "Error setting up microphone.", actions: ["try_again"] };
    }
};
